import { useCallback, useEffect, useRef, useState } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { traverse } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { JSONNode } from '@atlaskit/editor-json-transformer';
import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { StoreError } from '@atlassian/conversation-assistant-store/controllers/chat-signals';
import {
	CHAT_STORE_STATUS,
	isStoreError,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { useStagingAreaActions } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { useSmartCreationChatStoreApi } from '@atlassian/conversation-assistant/controllers/smart-creation-api';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { isRemixRestrictedNodeType } from '../remix/remix-config';
import {
	getSubTypeDisplayName,
	getTypeDisplayName,
	type RemixOptionSelectionParameters,
	// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
} from '../remix/remix-overlay';

import type { DynamicUiSource, RemixProductConfig, RemixProductIntegration } from './types';

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 */
export const filterRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isRemixRestrictedNodeType(adfNode.type)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

const isActionComplete = (chatMessages: StoreChatItem[]): boolean => {
	const hasWhiteboard =
		findLastAssistantAction(
			chatMessages,
			(action) =>
				action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
				action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
		) !== undefined;
	if (hasWhiteboard) {
		return true;
	}
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_COMPLETE') {
			return true;
		}
		if (message.type === 'HUMAN') {
			return false;
		}
	}
	return false;
};

const defaultShouldUseEphemeralPreview = (chatMessages: StoreChatItem[]): boolean => {
	for (let i = chatMessages.length - 1; i >= 0; i--) {
		const message = chatMessages[i];
		if (message.type === 'ACTION_PREPARE') {
			return message.action ? isRemixGeneratedFlow(message.action) : false;
		}
	}
	return false;
};

const defaultIsActionReady = (chatMessages: StoreChatItem[]): boolean =>
	isActionComplete(chatMessages);

export const useRemixToAIFC = ({
	config,
	onActionReady,
	productIntegration,
}: {
	config: RemixProductConfig;
	onActionReady?: () => void;
	productIntegration: RemixProductIntegration;
}): {
	closeRemixPreview: () => void;
	isRemixError: boolean;
	openRemixPreview: () => void;
	remixStoreError: StoreError | null;
	sendAIFCPrompt: ({
		userPrompt,
		dynamicUiSubtype,
		dynamicUiType,
		dynamicUiSource,
		interactionSource,
	}: {
		dynamicUiSource: DynamicUiSource | undefined;
		dynamicUiSubtype?: string;
		dynamicUiType: RemixOptionSelectionParameters['type'];
		interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
		userPrompt?: string;
	}) => Promise<void>;
	stopRemixing: () => void;
} => {
	const { sendMessage, createConversationOptimistically, abortRequests, activeConversation } =
		useSmartCreationChatStoreApi();
	const { setPrompt, setDynamicUiContext, setForceSkipAutoConversationSelection } =
		useStagingAreaActions();
	const { preferences } = useUserPreferences();
	const [remixConversationId, setRemixConversationId] = useState<string | undefined>(undefined);

	const activeRemixConversation =
		activeConversation?.id && activeConversation.id === remixConversationId
			? activeConversation
			: null;

	const [chatMessages] = useChatMessages({ conversationId: activeRemixConversation?.id ?? '' });

	const remixStoreError =
		chatMessages.find((message): message is StoreError => isStoreError(message)) ?? null;
	const isRemixError =
		activeRemixConversation?.status === CHAT_STORE_STATUS.deactivatedAgentError ||
		remixStoreError !== null;

	const actionReadyFired = useRef(false);
	const previousConversationId = useRef(activeRemixConversation?.id);

	useEffect(() => {
		if (previousConversationId.current !== activeRemixConversation?.id) {
			actionReadyFired.current = false;
			previousConversationId.current = activeRemixConversation?.id;
		}
	}, [activeRemixConversation?.id]);

	useEffect(() => {
		if (actionReadyFired.current || !onActionReady) {
			return;
		}

		const isActionReady =
			config.isActionReady?.(chatMessages) ?? defaultIsActionReady(chatMessages);

		if (isActionReady) {
			actionReadyFired.current = true;
			onActionReady();
		}
	}, [config, chatMessages, onActionReady]);

	const stopRemixing = useCallback(() => {
		abortRequests?.({ conversationId: activeRemixConversation?.id });
	}, [abortRequests, activeRemixConversation?.id]);

	const openRemixPreview = useCallback(() => {
		setForceSkipAutoConversationSelection(true);
		const shouldUseEphemeralPreview =
			config.shouldUseEphemeralPreview?.(chatMessages) ??
			defaultShouldUseEphemeralPreview(chatMessages);

		productIntegration.openRemixPreview({
			isFloating: shouldUseEphemeralPreview,
			remixConversationId: activeRemixConversation?.id,
		});
	}, [
		config,
		chatMessages,
		productIntegration,
		activeRemixConversation?.id,
		setForceSkipAutoConversationSelection,
	]);

	const sendAIFCPrompt = useCallback(
		async ({
			userPrompt,
			dynamicUiSubtype,
			dynamicUiType,
			dynamicUiSource,
			interactionSource,
		}: {
			dynamicUiSource: DynamicUiSource | undefined;
			dynamicUiSubtype?: string;
			dynamicUiType: RemixOptionSelectionParameters['type'];
			interactionSource?: import('@atlassian/conversation-assistant-instrumentation/constants/analytics').InteractionSource;
			userPrompt?: string;
		}) => {
			const defaultPromptText = 'Remix the selected content';
			const selectedDisplayName = dynamicUiSubtype
				? getSubTypeDisplayName(dynamicUiSubtype)
				: getTypeDisplayName(dynamicUiType);
			const promptText =
				dynamicUiType === 'custom'
					? userPrompt ?? defaultPromptText
					: `Remix the selected content as a ${selectedDisplayName}`;

			const prompt: DocNode = {
				type: 'doc',
				version: 1,
				content: [
					{
						type: 'paragraph',
						content: [
							{
								type: 'text',
								text: promptText,
							},
						],
					},
				],
			};

			const newConversationId = crypto.randomUUID();
			setRemixConversationId(newConversationId);
			actionReadyFired.current = false;

			await createConversationOptimistically({ id: newConversationId });

			sendMessage({
				prompt,
				storeUserMessages: preferences.storeUserMessages,
				conversationId: newConversationId,
				messageSources: {
					interactionSource,
					followUpSource: interactionSource,
				},
				productKey: config.productKey,
				contentContext: 'staging-area',
				creationContextParams: {
					dynamicUiType,
					dynamicUiSubtype,
					dynamicUiSource,
					source: 'remix',
				},
			});

			setPrompt('Remix');
			setDynamicUiContext(dynamicUiType, dynamicUiSubtype, dynamicUiSource);
		},
		[
			config.productKey,
			createConversationOptimistically,
			sendMessage,
			setPrompt,
			setDynamicUiContext,
			preferences.storeUserMessages,
		],
	);

	return {
		sendAIFCPrompt,
		stopRemixing,
		isRemixError,
		remixStoreError,
		openRemixPreview,
		closeRemixPreview: productIntegration.closeRemixPreview,
	};
};
